import { useId, useState } from 'react';
import { Container, Card, Row, Col, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { accessLogService } from '../services/accessLog.service';

const MAX_FUNNEL_DAYS = 31;

function getDefaultDateRange(): { start: string; end: string } {
  const end = new Date();
  const start = new Date(end);
  start.setDate(start.getDate() - 7);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

function daysBetween(start: string, end: string): number {
  const a = new Date(start);
  const b = new Date(end);
  return Math.ceil((b.getTime() - a.getTime()) / (24 * 60 * 60 * 1000));
}

export default function ConversionFunnel() {
  const { t, i18n } = useTranslation();
  const embedded = useLocation().pathname.startsWith('/admin/insights/');
  const fieldId = useId();
  const [startDate, setStartDate] = useState<string>(() => getDefaultDateRange().start);
  const [endDate, setEndDate] = useState<string>(() => getDefaultDateRange().end);

  const startParam = startDate ? `${startDate}T00:00:00` : '';
  const endParam = endDate ? `${endDate}T23:59:59` : '';
  const rangeDays = startDate && endDate ? daysBetween(startDate, endDate) : 0;
  const rangeTooLong = rangeDays > MAX_FUNNEL_DAYS;
  const canFetch = Boolean(
    startParam && endParam && startDate <= endDate && !rangeTooLong
  );

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['conversion-funnel', startParam, endParam],
    queryFn: () => accessLogService.getConversionFunnel({ startDate: startParam, endDate: endParam }),
    enabled: canFetch,
    staleTime: 60 * 1000,
  });

  const funnel = data?.data;
  const total = funnel?.totalUniqueIps ?? 0;
  const pct = (n: number) => (total > 0 ? ((n / total) * 100).toFixed(1) : '0.0');

  return (
    <Container className="py-4">
      {!embedded && (
        <>
          <h1 className="mb-4">
            {t('conversion_funnel.title', { defaultValue: '転換ファネル（IPベース）' })}
          </h1>
          <p className="text-muted mb-4">
            {t('conversion_funnel.description', {
              defaultValue:
                '期間内に公開ページを1回以上見たユニークIP数を分母に、トップのみ・登録ページ・利用規約・プライバシー・新規登録まで進んだ割合を表示します。',
            })}
          </p>
        </>
      )}

      <Card className="mb-4">
        <Card.Header>
          {t('conversion_funnel.date_range', { defaultValue: '期間' })}
        </Card.Header>
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={3}>
              <Form.Label htmlFor={`${fieldId}-start-date`}>
                {t('conversion_funnel.start_date', { defaultValue: '開始日' })}
              </Form.Label>
              <Form.Control
                id={`${fieldId}-start-date`}
                name="conversionFunnelStartDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                lang={i18n.language}
              />
            </Col>
            <Col md={3}>
              <Form.Label htmlFor={`${fieldId}-end-date`}>
                {t('conversion_funnel.end_date', { defaultValue: '終了日' })}
              </Form.Label>
              <Form.Control
                id={`${fieldId}-end-date`}
                name="conversionFunnelEndDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                lang={i18n.language}
              />
            </Col>
            <Col md={2}>
              <Button
                variant="primary"
                onClick={() => refetch()}
                disabled={!canFetch || isFetching}
                aria-busy={isFetching}
              >
                {isFetching
                  ? t('common.loading', { defaultValue: '読み込み中...' })
                  : t('conversion_funnel.apply', { defaultValue: '表示' })}
              </Button>
            </Col>
          </Row>
          {startDate && endDate && startDate > endDate && (
            <Alert variant="warning" className="mt-2 mb-0">
              {t('conversion_funnel.invalid_range', {
                defaultValue: '開始日は終了日以前にしてください。',
              })}
            </Alert>
          )}
          {startDate && endDate && startDate <= endDate && rangeTooLong && (
            <Alert variant="warning" className="mt-2 mb-0">
              {t('conversion_funnel.max_days', {
                defaultValue: `期間は{{max}}日以内で指定してください。`,
                max: MAX_FUNNEL_DAYS,
              })}
            </Alert>
          )}
        </Card.Body>
      </Card>

      {!canFetch && (
        <p className="text-muted">
          {t('conversion_funnel.select_range', {
            defaultValue: '開始日・終了日を選択して「表示」を押してください。',
          })}
        </p>
      )}

      {isError && (
        <Alert variant="danger">
          {t('conversion_funnel.error_loading', {
            defaultValue: 'データの取得に失敗しました。',
          })}
          {error &&
            (typeof (error as { response?: { data?: { message?: string } } }).response?.data
              ?.message === 'string'
              ? ` ${(error as unknown as { response: { data: { message: string } } }).response.data.message}`
              : (error as { code?: string; message?: string })?.code === 'ECONNABORTED' ||
                  /timeout/i.test(String((error as Error)?.message ?? ''))
                ? ` ${t('conversion_funnel.error_timeout', {
                    defaultValue:
                      'リクエストがタイムアウトしました。期間を短くするか、管理者にインデックス sm_access_logs_action_access_time_idx の適用を依頼してください。',
                  })}`
                : error instanceof Error
                  ? ` ${error.message}`
                  : '')}
        </Alert>
      )}

      {canFetch && isLoading && (
        <div className="text-center py-5" role="status">
          <Spinner animation="border" />
        </div>
      )}

      {canFetch && funnel && !isLoading && (
        <>
          <Card className="mb-3">
            <Card.Body>
              <Row>
                <Col>
                  <span className="text-muted">
                    {t('conversion_funnel.total_unique_ips', {
                      defaultValue: '期間内に公開ページを1回以上見たユニークIP数',
                    })}
                  </span>
                  <h4 className="mb-0 mt-1">{total.toLocaleString()}</h4>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Row>
            <Col md={6} lg={3} className="mb-3">
              <Card className="h-100">
                <Card.Header className="small">
                  {t('conversion_funnel.top_only', {
                    defaultValue: 'トップページだけ見てやめた人の割合',
                  })}
                </Card.Header>
                <Card.Body>
                  <h4 className="text-primary mb-0">{pct(funnel.topOnlyCount)}%</h4>
                  <small className="text-muted">
                    {funnel.topOnlyCount.toLocaleString()} / {total.toLocaleString()}
                  </small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3} className="mb-3">
              <Card className="h-100">
                <Card.Header className="small">
                  {t('conversion_funnel.register_abandoned', {
                    defaultValue: '新規登録画面でやめた人の割合',
                  })}
                </Card.Header>
                <Card.Body>
                  <h4 className="text-warning mb-0">{pct(funnel.registerPageViewedCount)}%</h4>
                  <small className="text-muted">
                    {funnel.registerPageViewedCount.toLocaleString()} / {total.toLocaleString()}
                  </small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3} className="mb-3">
              <Card className="h-100">
                <Card.Header className="small">
                  {t('conversion_funnel.terms_privacy_only', {
                    defaultValue: '利用規約・プライバシーを見てやめた割合',
                  })}
                </Card.Header>
                <Card.Body>
                  <h4 className="text-info mb-0">{pct(funnel.termsPrivacyOnlyCount)}%</h4>
                  <small className="text-muted">
                    {funnel.termsPrivacyOnlyCount.toLocaleString()} / {total.toLocaleString()}
                  </small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3} className="mb-3">
              <Card className="h-100">
                <Card.Header className="small">
                  {t('conversion_funnel.register_reached', {
                    defaultValue: '新規登録まで進んだ人の割合',
                  })}
                </Card.Header>
                <Card.Body>
                  <h4 className="text-success mb-0">{pct(funnel.registerPageViewedCount)}%</h4>
                  <small className="text-muted">
                    {funnel.registerPageViewedCount.toLocaleString()} / {total.toLocaleString()}
                  </small>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <p className="small text-muted mt-2">
            {t('conversion_funnel.note', {
              defaultValue:
                '「新規登録まで進んだ人」は新規登録ページ（/register）を開いたユニークIP数です。同一IPで複数ページを閲覧している場合は、各指標で重複してカウントされます。',
            })}
          </p>
        </>
      )}
    </Container>
  );
}
